import React, { useState, useEffect } from 'react';
import '../pages-styles/insights.css';
import { usePageMeta } from '../hooks/usePageMeta';

function Insights() {
  usePageMeta(
    'Football Insights & Updates',
    'Articles on football careers, transfers, development pathways, and football industry insights.'
  );
  
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNews, setSelectedNews] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Fetch news from backend
  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const apiUrl = process.env.REACT_APP_API_URL || '/api';
        const response = await fetch(`${apiUrl}/news/`);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const data = await response.json();
        // Sort by published date (newest first)
        const sortedNews = data.sort((a, b) => {
          const dateA = new Date(a.published_at || a.created_at);
          const dateB = new Date(b.published_at || b.created_at);
          return dateB - dateA;
        });
        setNews(sortedNews);
        setError(null);
      } catch (err) {
        console.error('Error fetching news:', err);
        setError('Failed to load articles. Please try again later.');
        // Fallback to empty state
        setNews([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, []);

  const openModal = (newsItem) => {
    setSelectedNews(newsItem);
    setShowModal(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedNews(null);
    document.body.style.overflow = 'unset';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  const getCategory = (title) => {
    // Extract category from title or use a default
    if (title.toLowerCase().includes('transfer')) return 'Transfers';
    if (title.toLowerCase().includes('develop')) return 'Development';
    if (title.toLowerCase().includes('market')) return 'Market';
    if (title.toLowerCase().includes('agent')) return 'Professional';
    if (title.toLowerCase().includes('career')) return 'Careers';
    if (title.toLowerCase().includes('international')) return 'International';
    return 'Updates';
  };

  return (
    <div className="insights-page">
      {/* Page Header */}
      <section className="page-intro">
        <h1>Football Insights & Updates</h1>
        <p>Latest articles on football careers, transfers, development pathways, and football industry insights.</p>
      </section>

      {/* Error Message */}
      {error && (
        <div className="error-banner">
          <p>{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading articles...</p>
        </div>
      )}

      {/* Insights Grid */}
      {!loading && news.length > 0 && (
        <section className="insights-grid">
          {news.map((newsItem) => {
            const imageUrl = newsItem.image?.startsWith('http') 
              ? newsItem.image 
              : `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}${newsItem.image}`;
            return (
            <article key={newsItem.id} className="insight-card">
              {newsItem.image && (
                <div className="insight-image">
                  <img src={imageUrl} alt={newsItem.title} />
                </div>
              )}
              <div className="insight-category">{getCategory(newsItem.title)}</div>
              <h3>{newsItem.title}</h3>
              <p className="insight-date">{formatDate(newsItem.published_at || newsItem.created_at)}</p>
              <p className="insight-excerpt">{newsItem.summary}</p>
              <button 
                className="read-more-btn"
                onClick={() => openModal(newsItem)}
              >
                Read More →
              </button>
            </article>
            );
          })}
        </section>
      )}

      {/* Empty State */}
      {!loading && news.length === 0 && !error && (
        <div className="empty-state">
          <p>No articles available at the moment. Check back soon!</p>
        </div>
      )}

      {/* Modal Detail View */}
      {showModal && selectedNews && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>✕</button>
            
            {selectedNews.image && (
              <div className="modal-image">
                <img src={selectedNews.image?.startsWith('http') 
                  ? selectedNews.image 
                  : `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}${selectedNews.image}`} alt={selectedNews.title} />
              </div>
            )}
            
            <div className="modal-body">
              <div className="modal-category">{getCategory(selectedNews.title)}</div>
              <h2>{selectedNews.title}</h2>
              <p className="modal-date">{formatDate(selectedNews.published_at || selectedNews.created_at)}</p>
              
              {selectedNews.author && (
                <p className="modal-author">By {selectedNews.author}</p>
              )}
              
              <div className="modal-summary">
                <h4>Summary</h4>
                <p>{selectedNews.summary}</p>
              </div>
              
              <div className="modal-article">
                <h4>Article</h4>
                <p>{selectedNews.content}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Newsletter Section */}
      <section className="newsletter-section">
        <h2>Stay Updated</h2>
        <p>Subscribe to receive the latest football insights and professional updates.</p>
        <form className="newsletter-form" onSubmit={(e) => e.preventDefault()}>
          <input 
            type="email" 
            placeholder="Enter your email" 
            required 
            aria-label="Email for newsletter"
          />
          <button type="submit" className="newsletter-btn">Subscribe</button>
        </form>
      </section>
    </div>
  );
}

export default Insights;
